import React from "react";
import {Link} from 'react-router-dom'

const GuessRushPrivacyPolicy = () => {
    const accountDeletionRequestEmail = process.env.REACT_APP_ACCOUNT_DELETION_REQUEST_EMAIL;
    const contactUsEmail = process.env.REACT_APP_CONTACT_US_EMAIL;
    const accountDeletionPageLink = process.env.REACT_APP_ACCOUNT_DELETION_PAGE_LINK;
    return (
        <div className="privacy-policy-main-container">
            <h3>Privacy Policy</h3>
            <h1 className="privacy-policy-main-title">GuessRush</h1>
            <p><b>Last updated 23 June 2026</b></p>
            <p>This privacy notice for Noble Notch Ltd ('we', 'us', or 'our'), describes how and why we might collect,
                store, use, and/or share ('process') your information when you use our services ('Services'), such as
                when you:</p>
            <ul>
                <li>Download and play our mobile game GuessRush ("Guess with us in 3 seconds"), or any other
                    application of ours that links to this privacy notice
                </li>
                <li>Engage with us in other related ways, including any sales, marketing, or events</li>
            </ul>
            <p><b>Questions or concerns?</b> Reading this privacy notice will help you understand your privacy rights
                and choices. If you do not agree with our policies and practices, please do not use our Services. If you
                still have any questions or concerns, please contact us at <a
                    href={"mailto:" + contactUsEmail}>{contactUsEmail}</a></p>

            <h4>Summary</h4>
            <p>GuessRush is a free, multi-category quiz game. <b>You do not need to create an account, sign in, or
                provide your name or email address to play.</b> The game can be played offline once content has loaded.
                We process a small amount of information so that the game's online features (leaderboards, the Daily
                Challenge and Weekly Challenges) and advertising can work:</p>
            <ul>
                <li>A randomly generated player ID and an optional display name you choose, used only to show you on the
                    leaderboards.
                </li>
                <li>Your game scores and challenge results, submitted to our servers for ranking.</li>
                <li>Advertising and device identifiers collected by our advertising partner (Unity Ads) to show ads.
                </li>
                <li>Your coins, lives, statistics and progress, which are stored locally on your own device.</li>
            </ul>
            <p>We do <b>not</b> collect your precise location, we do <b>not</b> require any sign-in, and we do
                <b> not</b> sell your personal information.</p>

            <h4>1. What Information Do We Collect?</h4>
            <h5>Information you provide to us</h5>
            <p><b>In Short:</b> We collect the limited information you choose to give us.</p>
            <ul>
                <li><b>Display name.</b> If you choose to appear on the online leaderboards, you may enter a display
                    name (a nickname). You do not have to use your real name, and you can play without entering one.
                </li>
                <li><b>Contact information.</b> If you email us for support or to make a request, we receive your email
                    address and the contents of your message.
                </li>
            </ul>
            <p><b>Sensitive Information.</b> We do not process sensitive information.</p>

            <h5>Information collected automatically</h5>
            <p><b>In Short:</b> Some information — such as device and advertising identifiers — is collected
                automatically when you use the game, mainly to operate online features and to show ads.</p>
            <ul>
                <li><b>Player identifier.</b> The game generates a random player ID on your device. It is used to
                    associate your scores and challenge results with you on the leaderboards. It is not derived from
                    your name, email, or phone number.
                </li>
                <li><b>Gameplay data.</b> When you submit a score or complete the Daily or Weekly Challenges, your score
                    and the relevant category are sent to our servers for ranking.
                </li>
                <li><b>Advertising and device data.</b> Our advertising partner, Unity Ads, automatically collects
                    information needed to serve and measure ads. This may include your device's advertising ID, device
                    model and manufacturer, operating system and version, language, coarse location inferred from your
                    IP address, and information about ads you are shown. Please see <b>Section 4</b> for details and a
                    link to Unity's own privacy policy.
                </li>
                <li><b>Log and diagnostic data.</b> Our servers may automatically record technical information such as
                    your IP address and request details when your device contacts them, for security, troubleshooting,
                    and to keep the Services running.
                </li>
            </ul>

            <h5>Information stored only on your device</h5>
            <p>Your coins balance, lives, achievements, statistics, no-repeat question history, and challenge progress
                are stored locally on your device using your device's standard app storage. This information stays on
                your device and is removed if you uninstall the game or clear its data.</p>

            <h4>2. How Do We Process Your Information?</h4>
            <p><b>In Short:</b> We process your information to run the game and its online features, to show ads, to keep
                the Services secure, and to comply with the law.</p>
            <ul>
                <li>To operate the leaderboards, Daily Challenge, and Weekly Challenges and show your ranking.</li>
                <li>To deliver and measure advertising, which keeps the game free to play.</li>
                <li>To deliver content (quiz questions and challenge sets) to your device.</li>
                <li>To send you optional local reminders (for example, a Daily Challenge reminder). These notifications
                    are generated on your device; you can turn them off at any time in your device settings.
                </li>
                <li>To protect our Services, including fraud monitoring and prevention.</li>
                <li>To respond to your enquiries and provide support.</li>
            </ul>

            <h4>3. What Legal Bases Do We Rely On?</h4>
            <p><b>In Short:</b> We only process your personal information when we have a valid legal reason to do so.</p>
            <p>If you are located in the EU or UK, the General Data Protection Regulation (GDPR) and UK GDPR require us to
                explain the valid legal bases we rely on. These may include:</p>
            <ul>
                <li><b>Consent.</b> Where you have given us permission to use your information for a specific purpose
                    (such as showing personalised ads where required, or sending reminders). You can withdraw your
                    consent at any time.
                </li>
                <li><b>Legitimate Interests.</b> To operate, secure, and improve the game, and to show advertising that
                    keeps it free, where these interests are not overridden by your rights.
                </li>
                <li><b>Legal Obligations.</b> Where processing is necessary to comply with our legal obligations.</li>
            </ul>

            <h4>4. When and With Whom Do We Share Your Information?</h4>
            <p><b>In Short:</b> We share information with the service providers that make the game work. We do not sell
                your personal information.</p>
            <ul>
                <li><b>Advertising — Unity Ads.</b> We use Unity Ads (provided by Unity Technologies) to show banner and
                    rewarded video ads. Unity acts as an independent advertising provider and collects and processes
                    device and advertising identifiers as described in its own policy. You can read how Unity handles
                    this data in the <a href="https://unity.com/legal/game-player-and-app-user-privacy-policy"
                                        target="_blank" rel="noopener noreferrer">Unity Game Player and App User Privacy
                        Policy</a>.
                </li>
                <li><b>App distribution — Google Play.</b> The game is distributed through Google Play, which may
                    collect information in accordance with <a href="https://policies.google.com/privacy"
                                                              target="_blank" rel="noopener noreferrer">Google's Privacy
                        Policy</a>.
                </li>
                <li><b>Our hosting servers.</b> Leaderboard and challenge data is processed on servers operated for us to
                    provide the online features.
                </li>
                <li><b>Business Transfers.</b> We may share or transfer your information in connection with a merger,
                    sale of company assets, financing, or acquisition of all or a portion of our business.
                </li>
                <li><b>Legal requirements.</b> We may disclose information where required to do so by law or to protect
                    our rights.
                </li>
            </ul>

            <h4>5. How Do We Handle Advertising and Tracking?</h4>
            <p>Ads keep GuessRush free. Depending on your region and consent, ads may be personalised (based on
                advertising identifiers) or non-personalised. You can limit ad tracking at the device level:</p>
            <ul>
                <li><b>Android:</b> Settings → Google → Ads, where you can reset or delete your advertising ID and opt
                    out of ad personalisation.
                </li>
            </ul>
            <p>Resetting or deleting your advertising ID does not stop ads from showing, but limits how they are
                personalised.</p>

            <h4>6. How Long Do We Keep Your Information?</h4>
            <p><b>In Short:</b> We keep information only for as long as necessary for the purposes set out in this
                notice.</p>
            <p>Leaderboard and challenge entries associated with your player ID are retained while they remain relevant
                to the rankings, unless you ask us to delete them. Information stored locally on your device remains
                until you uninstall the game or clear its data. When we no longer need your information, we delete or
                anonymise it.</p>

            <h4>7. How Do We Keep Your Information Safe?</h4>
            <p><b>In Short:</b> We use reasonable technical and organisational measures to protect your information.</p>
            <p>However, no electronic transmission or storage can be guaranteed to be 100% secure. Although we do our
                best to protect your information, transmission of information to and from our Services is at your own
                risk.</p>

            <h4>8. What Are Your Privacy Rights?</h4>
            <p><b>In Short:</b> Depending on where you live, you may have rights to access, correct, or delete your
                information.</p>
            <p>In some regions (such as the EEA, UK, Switzerland, and Canada), you have rights under applicable data
                protection laws, which may include the right to request access to, correction or deletion of your
                personal information, to restrict or object to its processing, and to data portability. To make a
                request, contact us using the details below or visit <Link to={"/" + accountDeletionPageLink}>the
                    account deletion request page</Link>.</p>
            <p>Because GuessRush has no login, deletion requests relating to leaderboard data are handled using your
                in-game player ID, which you can find or share with us from the game. If you are located in the EEA or
                UK and believe we are unlawfully processing your personal information, you also have the right to
                complain to your <a href="https://ec.europa.eu/newsroom/article29/items/612080"
                                    target="_blank" rel="noopener noreferrer">Member State data protection authority</a>
                {" "}or the <a href="https://ico.org.uk/make-a-complaint/data-protection-complaints/data-protection-complaints/"
                              target="_blank" rel="noopener noreferrer">UK data protection authority</a>.</p>

            <h4>9. Controls for Do-Not-Track Features</h4>
            <p>Most browsers and some operating systems include a Do-Not-Track ('DNT') feature. No uniform standard for
                recognising DNT signals has been finalised, so we do not currently respond to them. If a standard is
                adopted in the future, we will inform you in a revised version of this notice.</p>

            <h4>10. Do United States Residents Have Specific Privacy Rights?</h4>
            <p><b>In Short:</b> If you are a resident of certain US states (such as California, Colorado, Connecticut,
                Utah, or Virginia), you may have rights to access or delete your personal information and to opt out of
                certain processing.</p>
            <p>We have not sold, and will not sell, personal information. We do not knowingly process the sensitive
                personal information of consumers. To exercise any rights you may have, please email <a
                    href={"mailto:" + accountDeletionRequestEmail}>{accountDeletionRequestEmail}</a> or visit <Link
                    to={"/" + accountDeletionPageLink}>the account deletion request page</Link>. We will not
                discriminate against you for exercising your privacy rights.</p>

            <h4>11. Children</h4>
            <p>GuessRush is not directed to children under the age of 13 (or the equivalent minimum age in your
                jurisdiction), and we do not knowingly collect personal information from children. We encourage parents
                and guardians to monitor their children's use of the game. If you believe that a child has provided us
                with personal information, please contact us at <a
                    href={"mailto:" + contactUsEmail}>{contactUsEmail}</a> so that we can take appropriate action.</p>

            <h4>12. Do We Make Updates to This Notice?</h4>
            <p><b>In Short:</b> Yes, we will update this notice as necessary to stay compliant with relevant laws.</p>
            <p>We may update this privacy notice from time to time. The updated version will be indicated by an updated
                'Last updated' date and will be effective as soon as it is accessible. We encourage you to review this
                notice frequently.</p>

            <h4>13. How Can You Contact Us About This Notice?</h4>
            <p>If you have questions or comments about this notice, you may email us at <a
                href={"mailto:" + contactUsEmail}>{contactUsEmail}</a></p>

            <h4>14. How Can You Review, Update, or Delete the Data We Collect From You?</h4>
            <p>You may request to review, update, or delete the information associated with your in-game player ID.
                To make a request, please visit <Link to={"/" + accountDeletionPageLink}>the account deletion request
                    page</Link> or email <a
                    href={"mailto:" + accountDeletionRequestEmail}>{accountDeletionRequestEmail}</a>. You can also
                remove all locally stored data at any time by uninstalling the game or clearing its data in your device
                settings.</p>
        </div>
    );
}

export default GuessRushPrivacyPolicy;
